import { FormControlLabel, Grid, Radio, RadioGroup } from "@mui/material";
import { useCallback } from "react";
import { FormSection, FormSubSection } from "./FormComponents";
import PlatformSelectionPanel from "./PlatformSelectionPanel";

/**
 * TaskExecutionOptions component for configuring task execution settings
 *
 * This component provides a form interface with three main configuration sections:
 * - Database selection (main vs test database)
 * - Rerun options for handling completed tasks
 * - Platform selection for choosing execution environments
 *
 * @param {Object} props - Component props
 * @param {Object} props.selectedTaskData - Current task configuration data object
 * @param {Function} props.setSelectedTaskData - State setter function to update task configuration
 * @param {Array|Object} props.platformOptionData - Available platform configuration options
 * @param {Object} props.platformOptionsRefs - React refs for platform option elements
 *
 * @returns {JSX.Element} Form section containing task execution options
 */
const TaskExecutionOptions = ({ selectedTaskData, setSelectedTaskData, platformOptionData, platformOptionsRefs }) => {
  const handleDatabaseChange = useCallback(
    (event) => setSelectedTaskData((prev) => ({ ...prev, database: event.target.value })),
    [setSelectedTaskData]
  );

  const handleRerunValueChange = useCallback(
    (event) => setSelectedTaskData((prev) => ({ ...prev, rerun: event.target.value })),
    [setSelectedTaskData]
  );

  return (
    <FormSection title="Options">
      <Grid container direction={"column"} sx={{ pl: 1 }}>
        <FormSubSection title="Database">
          <RadioGroup value={selectedTaskData.database} onChange={handleDatabaseChange}>
            <FormControlLabel value="main" control={<Radio />} label="Add To Main Collection Database" />
            <FormControlLabel value="test" control={<Radio />} label="Add To Development Test Database" />
          </RadioGroup>
        </FormSubSection>

        <FormSubSection title="Rerun Completed Tasks">
          <RadioGroup value={selectedTaskData.rerun} onChange={handleRerunValueChange}>
            <FormControlLabel value="true" control={<Radio />} label="Rerun Completed Tasks" />
            <FormControlLabel value="false" control={<Radio />} label="Skip Completed Tasks" />
          </RadioGroup>
        </FormSubSection>

        <FormSubSection title="Platforms">
          <PlatformSelectionPanel
            platformOptionData={platformOptionData}
            platformOptionsRefs={platformOptionsRefs}
            selectedTaskData={selectedTaskData}
          />
        </FormSubSection>
      </Grid>
    </FormSection>
  );
};

export default TaskExecutionOptions;
